<template>
    <div>
        <div v-if="loaded">
            <!-- Row top buttons -->
            <div class="row q-mb-lg">
                <div class="col-6" style="justify-content: flex-start">
                    <q-btn
                        class="capitalize ctrl-button"
                        color="grey-5"
                        :label="`${$store.state.stringsStore.strings.action.toOverview}`"
                        @click="$router.push('/course')"
                        icon="keyboard_backspace"
                    />
                </div>
                <div class="col-6">
                    <q-btn
                        ref="save"
                        class="capitalize ctrl-button right q-mr-sm"
                        :color="
                            $v.data.$anyError || readOnly
                                ? 'grey-5'
                                : 'ctrl-turquoise'
                        "
                        :label="
                            readOnly
                                ? getString('action.edit')
                                : getString('action.save')
                        "
                        @click="saveChanges"
                        :icon-right="readOnly ? 'edit' : 'save'"
                    />
                    <q-btn
                        v-if="$route.params.id"
                        class="q-mr-md capitalize ctrl-button right"
                        icon-right="delete"
                        color="negative capitalize"
                        :label="$store.state.stringsStore.strings.action.delete"
                        align="right"
                        @click="removeCourse"
                    />
                    <q-btn
                      v-if="data.id"
                      class="q-mr-md capitalize ctrl-button right"
                      icon-right="arrow_forward"
                      color="info"
                      :label="getString(null, 'View training', 'Naar training')"
                      @click="goToCourse()"
                    />
                    <q-btn
                        v-if="data.preview_hash"
                        class="capitalize ctrl-button right q-mr-md"
                        :color="'primary'"
                        :label="getString('action.preview')"
                        icon="remove_red_eye"
                        @click="preview"
                    />
                </div>
            </div>

            <!-- Columns -->
            <div class="row gutter-sm q-mb-lg">
                <!-- Left -->
                <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
                    <div class="row q-mb-sm">
                        <div class="full-width">
                            <p class="card-title">
                                {{ getString("general") }}
                            </p>

                            <div class="card-block q-mb-md">
                                <div class="row">
                                    <q-field
                                        class="full-width"
                                        :error="
                                            $v.data.title.$dirty &&
                                            !$v.data.title.required
                                        "
                                        :errorLabel="
                                            $store.state.stringsStore
                                                .language === 'nl'
                                                ? 'Title is verplicht'
                                                : 'Title is required'
                                        "
                                    >
                                        <q-input
                                            :disable="readOnly"
                                            type="text"
                                            @input="suggestUrl"
                                            @blur="setUrl"
                                            :float-label="`${getString(
                                                'title'
                                            )} *`"
                                            v-model="data.title"
                                        />
                                    </q-field>
                                    <q-field
                                        class="full-width"
                                        :error="
                                            $v.data.url.$dirty &&
                                            !$v.data.url.required
                                        "
                                        :errorLabel="
                                            $store.state.stringsStore
                                                .language === 'nl'
                                                ? 'Url is verplicht'
                                                : 'Url is required'
                                        "
                                    >
                                        <!-- input -->
                                        <q-input
                                            :disable="readOnly"
                                            type="text"
                                            :float-label="`${getString(
                                                'url'
                                            )} *`"
                                            v-model="url"
                                        />
                                    </q-field>
                                </div>

                                <!-- Tags -->
                                <div class="row">
                                    <div class="col-md-12">
                                        <chips-autocomplete
                                            class="q-pb-md"
                                            :disable="readOnly"
                                            :selection="data.tags"
                                            :name="'Tags'"
                                            :labelTerm="'label'"
                                            :endpoint="'tag'"
                                            :resultLabel="'tags'"
                                            :promptNewMessage="
                                                getString('module.tags.add')
                                            "
                                        />
                                    </div>
                                </div>
                                <div class="row">
                                    <div class="col-md-12">
                                        <!-- Targets -->
                                        <q-select
                                            multiple
                                            :disable="readOnly"
                                            :readonly="readOnly"
                                            v-model="data.targets"
                                            :options="targetOptions"
                                            :float-label="
                                                getString(
                                                    'editor.block.header.targets'
                                                )
                                            "
                                        />
                                    </div>
                                </div>

                                <!-- Certificate -->
                                <div class="row">
                                    <div class="col-md-12">
                                      <q-field
                                            class="q-mt-md"
                                        >
                                            <q-search
                                                v-model="data.certificate.name"
                                                clearable
                                                @clear="clearCertificate"
                                                :disable="
                                                    readOnly ||
                                                    data.sort === 'webinar'
                                                "
                                                :float-label="getString(null, 'Certificate', 'Certificaat')"
                                            >
                                                <q-autocomplete
                                                    value-field="label"
                                                    :max-results="25"
                                                    :min-characters="2"
                                                    @search="
                                                        autocompleteCertificate
                                                    "
                                                    @selected="selectCertificate"
                                                />
                                            </q-search>
                                        </q-field>
                                    </div>
                                </div>

                                <div class="row q-mt-md q-mb-md">
                                    <q-field>
                                        <q-toggle
                                            :disable="readOnly"
                                            v-model="data.registration"
                                            color="secondary"
                                            :label="
                                                getString(
                                                    'module.course.details.registration'
                                                )
                                            "
                                        />
                                    </q-field>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row q-mb-sm">
                        <div class="full-width">
                            <p class="card-title">
                                {{ getString("action.details") }}
                            </p>
                            <div class="card-block q-mb-md">
                                <div class="row">
                                    <div class="col-md-6">
                                        <q-field
                                            class="full-width"
                                            :error="
                                                $v.data.type.$dirty &&
                                                !$v.data.type.required
                                            "
                                            :errorLabel="
                                                $store.state.stringsStore
                                                    .language === 'nl'
                                                    ? 'Type is verplicht'
                                                    : 'Type is required'
                                            "
                                        >
                                            <!-- input -->
                                            <q-select
                                                :disable="readOnly"
                                                :options="types"
                                                float-label="Type *"
                                                v-model="data.type"
                                            />
                                        </q-field>
                                    </div>
                                    <div class="col-md-6 q-pl-md">
                                        <q-field
                                            class="full-width"
                                            :errorLabel="
                                                $store.state.stringsStore
                                                    .language === 'nl'
                                                    ? 'Sort is verplicht'
                                                    : 'Sort is required'
                                            "
                                        >
                                            <!-- input -->
                                            <q-select
                                                :disable="readOnly"
                                                :options="sorts"
                                                :float-label="`${getString(
                                                    'module.course.details.sort'
                                                )} *`"
                                                v-model="data.sort"
                                                @input="selecteSort"
                                                :error="
                                                    $v.data.sort.$dirty &&
                                                    !$v.data.sort.required
                                                "
                                            />
                                        </q-field>
                                    </div>
                                </div>

                                <div
                                    class="row"
                                    v-show="data.sort == 'webinar'"
                                >
                                    <div class="col-md-12">
                                        <q-field
                                            class="full-width"
                                            :errorLabel="
                                                $store.state.stringsStore
                                                    .language === 'nl'
                                                    ? 'Url is verplicht voor soort - webinar'
                                                    : 'Url is required for sort - webinar'
                                            "
                                        >
                                            <!-- input -->
                                            <q-input
                                                :disable="readOnly"
                                                type="text"
                                                float-label="Webinar Url *"
                                                v-model="webinar_url"
                                                :error="
                                                    $v.data.webinar_url
                                                        .$dirty &&
                                                    !$v.data.webinar_url
                                                        .required
                                                "
                                            />
                                        </q-field>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md-12">
                                        <q-input
                                            :disable="readOnly"
                                            type="textarea"
                                            class="full-width q-mb-md"
                                            :float-label="
                                                getString(
                                                    'module.course.details.implementation'
                                                )
                                            "
                                            v-model="data.implementation"
                                        />
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md-8">
                                        <q-field
                                            class="q-mb-md"
                                            :errorLabel="
                                                $store.state.stringsStore
                                                    .language === 'nl'
                                                    ? 'Locatie is verplicht'
                                                    : 'Location is required'
                                            "
                                        >
                                            <q-search
                                                v-model="data.location.name"
                                                clearable
                                                @clear="clearLocation"
                                                :disable="
                                                    readOnly ||
                                                    data.sort === 'webinar'
                                                "
                                                :float-label="`${getString(
                                                    'block.type.button.location'
                                                )} *`"
                                                :error="
                                                    $v.data.location.id
                                                        .$dirty &&
                                                    !$v.data.location.id
                                                        .required
                                                "
                                            >
                                                <q-autocomplete
                                                    value-field="label"
                                                    :max-results="25"
                                                    :min-characters="2"
                                                    @search="
                                                        autocompleteLocation
                                                    "
                                                    @selected="selectLocation"
                                                />
                                            </q-search>
                                        </q-field>
                                    </div>
                                    <div class="col-md-4 q-pl-md">
                                        <q-select
                                            :disable="readOnly"
                                            :options="paths"
                                            clearable
                                            class="full-width q-mb-md"
                                            :float-label="
                                                getString(
                                                    'module.course.details.learningPath'
                                                )
                                            "
                                            v-model="data.learning_path"
                                        />
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md-6">
                                        <q-input
                                            :disable="readOnly"
                                            type="number"
                                            @input="data.duration.value = Math.ceil(data.duration.value)"
                                            class="full-width q-mb-md"
                                            :float-label="`${getString(
                                                'module.course.details.duration'
                                            )} *`"
                                            v-model="data.duration.value"
                                            :error="
                                                $v.data.duration.value.$dirty &&
                                                !$v.data.duration.value.required
                                            "
                                        />
                                    </div>
                                    <div class="col-md-6 q-pl-md">
                                        <q-select
                                            :disable="readOnly"
                                            :options="duration_units"
                                            class="full-width q-mb-md"
                                            :float-label="`${getString(
                                                'module.course.details.period.label'
                                            )} *`"
                                            v-model="data.duration.unit"
                                            :error="
                                                $v.data.duration.unit.$dirty &&
                                                !$v.data.duration.unit.required
                                            "
                                        />
                                    </div>
                                </div>

                                <!-- Instructors -->
                                <div class="row">
                                    <div class="col-md-12">
                                        <chips-autocomplete
                                            :key="instructorKey"
                                            class="q-pb-md"
                                            :disable="readOnly"
                                            :selection="data.instructors"
                                            :name="`${getString('course.instructors')} *`"
                                            :labelTerm="'name'"
                                            :endpoint="'course/instructor'"
                                            :resultLabel="'instructors'"
                                            customPost
                                            :promptNewMessage="getString(null, 'Add extrenal instructor', 'Externe docent toevoegen')"
                                            :error="
                                                $v.data.instructors.$dirty &&
                                                !$v.data.instructors.required
                                            "
                                            :errorLabel="getString(null, 'Select at least one instructor', 'Selecteer minimaal één instructeur')"
                                            @add="promptCustomInstructor"
                                            @removed="removeExternalInstructor"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Summary points -->
                    <div class="row q-mb-sm">
                        <div class="full-width">
                            <p class="card-title">
                                {{ getString("course.summaryPoints") }}
                            </p>
                            <div class="card-block q-mb-md">
                                <div
                                    class="row"
                                    v-for="(point, p) in data.summary_points"
                                    :key="p"
                                >
                                    <div class="col-11">
                                        <q-field
                                            class="full-width"
                                            :count="255"
                                        >
                                            <q-input
                                                type="text"
                                                :float-label="`Point ${p + 1}`"
                                                maxlength="255"
                                                v-model="point.text"
                                            >
                                            </q-input>
                                        </q-field>
                                    </div>

                                    <div class="col-1">
                                        <q-btn
                                            @click="removeSummaryPoint(p)"
                                            color="negative"
                                            :disable="readOnly"
                                            round
                                            icon="close"
                                            size="xs"
                                            class="
                                                q-mr-sm q-ml-sm q-mt-md
                                                capitalize
                                            "
                                        />
                                    </div>
                                </div>
                                <q-btn
                                    @click="addSummaryPoint()"
                                    color="positive"
                                    :disable="readOnly"
                                    icon="add"
                                    :label="getString('action.add')"
                                    size="md"
                                    class="q-mr-sm q-mt-md capitalize"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Right -->
                <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
                    <div class="full-width">
                        <p class="card-title">
                            {{ getString("block.field.default.header.image") }}
                        </p>
                        <div class="card-block q-mb-md scroll">
                            <div class="row">
                                <div class="col-md-8 q-mr-md">
                                    <div
                                        class="
                                            card-block
                                            image-preview image
                                            flex-center
                                        "
                                        :class="
                                            $v.data.$dirty &&
                                            !data.image.location &&
                                            'card-block__error border-solid'
                                        "
                                    >
                                        <img
                                            v-if="data.image"
                                            class="
                                                image-preview__small
                                                centered
                                            "
                                            :src="`${buildFileLocation(
                                                data.image.location
                                            )}?timestamp=${Date.now()}`"
                                            alt=""
                                        />
                                    </div>
                                    <div v-show="$v.data.$dirty && !data.image.location" class="row q-mt-sm field-error">
                                        {{ $store.state.stringsStore.language === 'en' ? 'Image is required' : 'Afbeelding is verplicht' }}
                                    </div>
                                </div>
                                <div class="col">
                                    <q-btn
                                        class="capitalize ctrl-button"
                                        :color="readOnly ? 'grey-5' : 'primary'"
                                        :disabled="readOnly"
                                        :label="
                                            getString(
                                                'editor.button.image.select'
                                            )
                                        "
                                        @click="
                                            !readOnly &&
                                                setModalContext(null, true)
                                        "
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Publication block -->
                        <div class="row q-mb-sm">
                            <div class="full-width">
                                <p class="card-title">
                                    {{
                                        getString(
                                            "editor.block.header.publication"
                                        )
                                    }}
                                </p>
                                <div class="card-block scroll">
                                    <div class="row">
                                        <div class="col q-mr-sm">
                                            <q-select
                                                :disable="readOnly"
                                                :readonly="readOnly"
                                                class="full-width q-mb-md"
                                                placeholder="Publish"
                                                :float-label="
                                                    getString(
                                                        'module.blog.details.publication.date.datepicker.label'
                                                    )
                                                "
                                                v-model="data.status"
                                                :options="publicationOptions"
                                                @input="publicationChange()"
                                            />
                                        </div>
                                        <div class="col q-ml-sm">
                                            <!-- Publication date -->
                                            <q-datetime
                                                format24h
                                                :min="new Date(new Date().setHours(9,0,0,0))"
                                                class="full-width q-mb-md"
                                                :disable="
                                                    this.data.status !==
                                                        'published' || readOnly
                                                "
                                                :readonly="
                                                    this.data.status !==
                                                    'published'
                                                "
                                                :float-label="
                                                    getString(
                                                        'module.blog.details.publication.date.datepicker.label'
                                                    )
                                                "
                                                v-model="data.datetime_online"
                                                type="datetime"
                                            />
                                        </div>
                                    </div>

                                    <!-- Course contacts -->
                                    <div class="row">
                                        <div class="col-md-12">
                                          <chips-autocomplete
                                              :key="instructorKey"
                                              class="q-pb-md"
                                              :disable="readOnly"
                                              :selection="data.contacts"
                                              :name="getString(null, 'Contacts', 'Contacten')"
                                              :labelTerm="'fullname'"
                                              endpoint="contact"
                                              :resultLabel="'contacts'"
                                          />
                                        </div>
                                    </div>
                                    <div class="row">
                                      <div class="col">
                                        <q-toggle
                                            class="q-mt-md"
                                            color="secondary"
                                            v-model="data.internal"
                                            :disable="readOnly"
                                            :readonly="readOnly"
                                        >
                                            <span style="margin-left: 1em">
                                                {{
                                                    getString(
                                                        "pubilcation.internal"
                                                    )
                                                }}
                                            </span>
                                        </q-toggle>
                                      </div>
                                      <div class="col">
                                        <q-toggle
                                            class="q-mt-md"
                                            color="secondary"
                                            v-model="data.hidden"
                                            :disable="readOnly"
                                            :readonly="readOnly"
                                        >
                                            <span style="margin-left: 1em">
                                                {{
                                                    getString(
                                                        "hidden"
                                                    )
                                                }}
                                            </span>
                                        </q-toggle>
                                      </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Metadata -->
                        <p class="card-title">
                            {{ getString("editor.block.header.meta") }}
                        </p>
                        <div class="card-block q-mb-md scroll">
                            <div class="row">
                                <div class="col-md-6 q-pr-md">
                                    <q-input
                                        :disable="readOnly"
                                        type="text"
                                        class="no-shadow full-width q-mb-md"
                                        :float-label="
                                            getString('editor.meta.seo.title')
                                        "
                                        v-model="data.meta.seo.title"
                                    />
                                    <q-input
                                        :disable="readOnly"
                                        type="text"
                                        class="full-width q-mb-md"
                                        :float-label="
                                            getString('editor.meta.og.image')
                                        "
                                        clearable
                                        v-model="data.meta.og.image.name"
                                        @click="setModalContext('meta', true)"
                                    />
                                    <q-input
                                        :disable="readOnly"
                                        type="text"
                                        class="no-shadow full-width q-mb-md"
                                        :float-label="
                                            getString('editor.meta.og.title')
                                        "
                                        v-model="data.meta.og.title"
                                    />
                                    <q-checkbox
                                        :disable="readOnly"
                                        :readonly="readOnly"
                                        class="full-width q-mb-md"
                                        :label="
                                            getString('editor.meta.no_index')
                                        "
                                        v-model="data.meta.no_index"
                                    />
                                </div>
                                <div class="col-md-6 q-pl-md">
                                    <q-input
                                        :disable="readOnly"
                                        type="textarea"
                                        class="full-width q-mb-md"
                                        :float-label="
                                            getString(
                                                'editor.meta.seo.description'
                                            )
                                        "
                                        v-model="data.meta.seo.description"
                                    />
                                    <q-input
                                        :disable="readOnly"
                                        type="textarea"
                                        class="full-width q-mb-md"
                                        :float-label="
                                            getString(
                                                'editor.meta.og.description'
                                            )
                                        "
                                        v-model="data.meta.og.description"
                                    />
                                    <q-input
                                        :disable="readOnly"
                                        type="text"
                                        class="full-width q-mb-md"
                                        :float-label="
                                            getString('editor.meta.canonical')
                                        "
                                        v-model="data.meta.canonical"
                                    />
                                </div>
                            </div>
                        </div>

                        <p class="card-title">
                            {{ getString("module.course.details.price") }}
                        </p>

                        <div class="card-block q-mb-md">
                            <div class="row">
                                <div class="col-md-12">
                                    <q-input
                                        prefix="€"
                                        :disable="readOnly"
                                        type="number"
                                        class="full-width q-mb-md"
                                        :float-label="
                                            getString(
                                                'module.course.details.price'
                                            )
                                        "
                                        v-model="data.price.value"
                                    />
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-12">
                                    <q-field count>
                                        <q-input
                                            :disable="readOnly"
                                            type="textarea"
                                            class="full-width q-mb-md"
                                            :float-label="
                                                getString(
                                                    'module.course.details.priceExplaination'
                                                )
                                            "
                                            v-model="
                                                data.price.price_explanation
                                            "
                                        />
                                    </q-field>
                                </div>
                            </div>
                            <div class="row q-mb-sm q-mt-sm">
                                <div class="col-md-6 col-xs-12">
                                    <q-field>
                                        <q-toggle
                                            :disable="readOnly"
                                            v-model="
                                                data.price.price_on_request
                                            "
                                            color="secondary"
                                            :label="
                                                getString(
                                                    'module.course.details.priceOnRequest'
                                                )
                                            "
                                        />
                                    </q-field>
                                </div>
                                <div class="col-md-6 col-xs-12">
                                    <q-field>
                                        <q-toggle
                                            :disable="readOnly"
                                            v-model="
                                                data.price.subsidy_possible
                                            "
                                            color="secondary"
                                            :label="
                                                getString(
                                                    'module.course.details.subsidy'
                                                )
                                            "
                                        />
                                    </q-field>
                                </div>
                            </div>

                            <div
                                class="row"
                                v-show="data.price.subsidy_possible"
                            >
                                <div class="col-md-12">
                                    <q-field count>
                                        <q-input
                                            :disable="readOnly"
                                            type="textarea"
                                            class="full-width q-mb-md"
                                            :float-label="
                                                getString(
                                                    'module.course.details.subsidyExplaination'
                                                )
                                            "
                                            v-model="
                                                data.price.subsidy_explanation
                                            "
                                        />
                                    </q-field>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Program -->
                    <p class="card-title">
                        {{ getString("module.course.details.program") }}
                    </p>
                    <div class="card-block q-mb-md">
                        <div
                            v-for="(program, p) in data.program"
                            :key="p"
                            class="row q-mb-md"
                        >
                            <div class="col-md-6">
                                <q-input
                                    :disable="readOnly"
                                    type="textarea"
                                    class="full-width q-mr-sm"
                                    :float-label="getString('title')"
                                    v-model="program.title"
                                />
                            </div>
                            <div class="col-md-5 q-pr-md q-pl-md">
                                <q-datetime
                                    format24h
                                    type="time"
                                    :disable="readOnly"
                                    class="full-width"
                                    :float-label="$isEnglish ? 'Time' : 'Tijd'"
                                    v-model="program.datetime"
                                />
                            </div>
                            <div class="col-md-1 flex-center">
                                <q-btn
                                    icon="clear"
                                    :disabled="readOnly"
                                    color="negative"
                                    @click="
                                        readOnly ? null : removeProgramItem(p)
                                    "
                                    round
                                    size="xs"
                                />
                            </div>
                        </div>

                        <div class="row q-mt-md">
                            <q-btn
                                @click="readOnly ? null : addProgramItem()"
                                color="positive"
                                :disable="readOnly"
                                icon="add"
                                :label="getString('action.add')"
                                size="md"
                                type="submit"
                                class="q-mr-sm capitalize"
                            />
                        </div>
                    </div>
                    <div v-if="$route.params.id">
                        <p class="card-title">
                            {{ getString("course.documents") }}
                        </p>

                        <div class="card-block">
                            <table class="q-mb-md">
                                <tr>
                                    <th>{{ getString("name") }}</th>
                                    <th>
                                        {{
                                            $isEnglish
                                                ? "Availability"
                                                : "Beschikbaarheid"
                                        }}
                                    </th>
                                    <th></th>
                                </tr>
                                <p class="q-mt-md" v-show="!data.files.length">
                                    {{
                                        $isEnglish
                                            ? "No files selected"
                                            : "Geen bestanden geselecteerd"
                                    }}
                                </p>
                                <tr v-for="file in data.files" :key="file.id">
                                    <td>{{ file.name }}</td>
                                    <td>
                                        <q-toggle
                                            :disable="readOnly"
                                            v-model="file.availablity"
                                            true-value="pre"
                                            @input="toggleFilePrePost(file)"
                                            false-value="post"
                                            color="secondary"
                                            :label="
                                                file.availablity === 'pre'
                                                    ? 'Pre'
                                                    : 'Post'
                                            "
                                        />
                                    </td>
                                    <td>
                                        <q-btn
                                            icon="clear"
                                            :disabled="readOnly"
                                            color="negative"
                                            @click="
                                                readOnly
                                                    ? null
                                                    : removeFile(file.id)
                                            "
                                            round
                                            size="xs"
                                        />
                                    </td>
                                </tr>
                            </table>

                            <div class="dropzone" ref="dropzone">
                                <input
                                    type="file"
                                    ref="buttonUpload"
                                    v-show="false"
                                    @change="uploadFiles(false)"
                                    multiple
                                />

                                <q-btn
                                    @click="startUpload()"
                                    color="positive"
                                    :disable="readOnly"
                                    icon="add"
                                    :label="getString('action.add')"
                                    size="md"
                                    class="q-mr-sm capitalize"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Editors -->
            <div class="row q-mb-sm">
                <div class="full-width">
                    <p class="card-title">
                        {{ getString("module.course.details.content.general") }}
                    </p>

                    <div class="card-block">
                        <div class="row">
                            <editor
                                tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                                :init="tinyConfig()"
                                ref="tm"
                                v-model="data.content.general"
                            >
                            </editor>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row q-mb-sm">
                <div class="full-width">
                    <p class="card-title">
                        {{ getString("module.course.details.content.program") }}
                    </p>

                    <div class="card-block">
                        <div class="row">
                            <editor
                                tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                                :init="tinyConfig()"
                                ref="tm"
                                v-model="data.content.program"
                            >
                            </editor>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row q-mb-sm">
                <div class="full-width">
                    <p class="card-title">
                        {{
                            getString("module.course.details.content.admission")
                        }}
                    </p>

                    <div class="card-block">
                        <div class="row">
                            <editor
                                tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                                :init="tinyConfig()"
                                ref="tm"
                                v-model="data.content.admission_level"
                            >
                            </editor>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row q-mb-sm">
                <div class="full-width">
                    <p class="card-title">
                        {{
                            getString("module.course.details.content.material")
                        }}
                    </p>

                    <div class="card-block">
                        <div class="row">
                            <editor
                                tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                                :init="tinyConfig()"
                                ref="tm"
                                v-model="data.content.learning_material"
                            >
                            </editor>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row q-mb-sm">
                <div class="full-width">
                    <p class="card-title">
                        {{
                            getString(
                                "module.course.details.content.certification"
                            )
                        }}
                    </p>

                    <div class="card-block">
                        <div class="row">
                            <editor
                                tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                                :init="tinyConfig()"
                                ref="tm"
                                v-model="data.content.testing_certifiation"
                            >
                            </editor>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="loader" v-else>
            <q-spinner color="grey-5" :size="80" />
        </div>
        <!-- image select modal -->
        <q-modal v-if="imageModal" v-model="imageModal">
            <file-select-modal :fileType="'image'" :guid="modalContext" />
        </q-modal>

        <!-- Modal for external instructor -->
        <q-modal
          no-esc-dismiss
          no-backdrop-dismiss
          v-model="showCustomInstructorModal">
          <div class="q-ma-lg">
            <div class="row">
              <p>{{ getString(null, 'Add extrenal instructor', 'Externe docent toevoegen')}}</p>
            </div>
          <q-field
            :error="$v.newCustomInstructor.firstname.$invalid"
            :error-label="getString(null, 'Firstname is required', 'Voornaam is vereist')">
            <q-input
              :float-label="getString('firstname')"
              v-model="newCustomInstructor.firstname"
            ></q-input>
          </q-field>
          <q-field
            :error="$v.newCustomInstructor.lastname.$invalid"
            :error-label="getString(null, 'Lastname is required', 'Achternaam is vereist')">
            <q-input
              :float-label="getString('surname')"
              v-model="newCustomInstructor.lastname"
            ></q-input>
          </q-field>
          <q-field
            :error="$v.newCustomInstructor.email.$invalid"
            :error-label="getString(null, 'A valid email is required', 'Een geldig e-mailadres is vereist')">
            <q-input
              :float-label="getString('email')"
              :error="$v.newCustomInstructor.email.$dirty && $v.newCustomInstructor.email.$invalid"
              v-model="newCustomInstructor.email"
            ></q-input>
          </q-field>
            <div class="row q-mt-md" style="display: flex; justify-content: flex-end;">
              <q-btn
                  class="capitalize ctrl-button right q-mr-sm"
                  color="negative"
                  label="Cancel"
                  @click="showCustomInstructorModal = false"
              />
              <q-btn
                  class="capitalize ctrl-button right q-mr-sm"
                  color="ctrl-turquoise"
                  label="OK"
                  :disable="$v.newCustomInstructor.$anyError"
                  @click="addCustomInstructor"
              />
            </div>
          </div>
        </q-modal>

    </div>
</template>

<script>
import moment from 'moment';
import qs from 'qs';
import { required, requiredIf, requiredUnless, minLength, email } from 'vuelidate/lib/validators';

// Mixins
import saveChanges from '@/mixins/detailViews/saveChanges';
import fetchDetail from '@/mixins/detailViews/fetchDetail';
import getString from '@/helpers/GetStrings';
import EventBus from '@/event-bus';
import createUuid from '@/helpers/createUuid';
import upload from '@/mixins/library/upload';
import suggestUrl from '@/mixins/suggestUrl';

import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';

import college from '@/plugins/api/college/college';

const Editor = require('@tinymce/tinymce-vue').default;

export default {
  mixins: [fetchDetail, saveChanges, upload, college, suggestUrl],
  components: {
    ChipsAutocomplete,
    Editor,
    FileSelectModal,
  },
  validations: {
    newCustomInstructor: {
      email: {
        email,
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
      firstname: {
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
      lastname: {
        required: requiredIf(function () {
          return this.showCustomInstructorModal;
        }),
      },
    },
    data: {
      title: {
        required,
      },
      url: {
        required,
      },
      datetime_online: {
        required,
      },
      duration: {
        value: {
          required,
        },
        unit: {
          required,
        },
      },
      type: {
        required,
      },
      webinar_url: {
        required: requiredIf(function () {
          return this.data.sort === 'webinar';
        }),
      },
      sort: {
        required,
      },
      location: {
        id: {
          required: requiredUnless(function () {
            return this.data.sort === 'webinar';
          }),
        },
      },
      instructors: {
        required,
        minLength: minLength(1),
      },
    },
  },
  data() {
    return {
      instructorKey: 0,
      urlSet: false,
      imageModal: false,
      modalContext: null,
      loaded: false,
      guid: createUuid(),
      targetOptions: [],
      readOnly: true,
      // Name of the module defined in user rights. eg. blog for blog.edit, blog.add etc..
      permission: 'course',
      // Where data is fetched from
      endpoint: 'course',
      // The object key from the response object
      responseData: {
        single: 'course',
        multiple: 'courses',
      },
      // Used for navigation on the front-end (matching the router)
      module: 'course',
      types: [
        { label: getString('course.type.training'), value: 'training' },
        { label: getString('course.type.course'), value: 'course' },
        { label: getString('course.type.workshop'), value: 'workshop' },
      ],
      sorts: [
        { label: getString('course.sort.practical'), value: 'practical' },
        { label: getString('course.sort.theory'), value: 'theory' },
        { label: getString('course.sort.theoryPractical'), value: 'practical-theory' },
        { label: getString('course.sort.webinar'), value: 'webinar' },
      ],
      paths: [
        { label: 'BBL', value: 'BBL' },
        { label: 'BOL', value: 'BOL' },
      ],
      duration_units: [
        { label: getString('module.course.details.period.minute'), value: 'minute' },
        { label: getString('module.course.details.period.hour'), value: 'hour' },
        { label: getString('module.course.details.period.day'), value: 'day' },
        { label: getString('module.course.details.period.week'), value: 'week' },
        { label: getString('module.course.details.period.month'), value: 'month' },
        { label: getString('module.course.details.period.year'), value: 'year' },
      ],
      publicationOptions: [
        {
          label: getString('publication.option.draft'),
          value: 'draft',
        },
        {
          label: getString('publication.option.select'),
          value: 'published',
        },
      ],
      data: {
        title: '',
        url: '',
        type: null,
        sort: null,
        online: false,
        internal: false,
        image: {
          cation: null,
        },
        certificate: {
          id: null,
          name: '',
        },
        webinar_url: null,
        datetime_online: null,
        location: {
          name: '',
          id: null,
        },
        learning_path: null,
        implementation: '',
        targets: [],
        summary_points: [],
        program: [],
        files: [],
        duration: {
          value: null,
          duration_unit: null,
        },
        registration: false,
        instructors: [],
        tags: [],
        price: {
          value: null,
          on_request: false,
          explanation: '',
          subsidy_possible: false,
          subsidy_explanation: null,
        },
        content: {
          general: null,
          program: null,
          learning_material: null,
          testing_certifiation: null,
          admission_level: null,
        },
        meta: {
          seo: {
            title: null,
            description: null,
          },
          og: {
            title: null,
            description: null,
            image: {
              name: '',
              location: '',
            },
          },
          canonical: null,
          no_index: null,
        },
      },
      labelSet: false,
      plugins: [],
    };
  },

  methods: {
    getString,

    goToCourse() {
      window.open(this.codeUrl, '_blank').focus();
    },

    selecteSort() {
      if (this.data.sort === 'webinar') {
        this.clearLocation();
      }
      this.$v.$touch();
    },

    clearLocation() {
      this.data.location = { id: null, name: null };
    },

    clearCertificate() {
      this.data.certificate = { id: null, name: null };
    },

    addSummaryPoint() {
      if (!this.data.summary_points) {
        this.data.summary_points = [];
      }
      this.data.summary_points.push({ text: '' });
    },

    preview() {
      const site = process.env.VUE_APP_SITE;
      const url = `${site}college/preview/${this.data.preview_hash}`;
      const win = window.open(url, '_blank');
      win.focus();
    },

    removeSummaryPoint(index) {
      this.data.summary_points.splice(index, 1);
    },

    refreshFiles() {
      if (this.$route.params.id) {
        this.axios.get(`course/${this.$route.params.id}`).then((res) => {
          this.data.files = res.data.course.files;
        });
      }
    },

    toggleFilePrePost(file) {
      this.axios
        .put(
          `course/${this.$route.params.id}/upload/${file.id}`,
          qs.stringify({ available: file.availablity }),
        )
        .then(() => {});
    },

    removeFile(fileId) {
      this.axios.delete(`course/${this.$route.params.id}/upload/${fileId}`).then(() => {
        this.refreshFiles();
      });
    },

    startUpload() {
      this.$refs.buttonUpload.click();
    },

    addProgramItem() {
      if (!this.data.program) {
        this.data.program = [];
      }
      this.data.program.push({ title: '', datetime: Date.now() });
    },

    removeProgramItem(index) {
      this.data.program.splice(index, 1);
    },

    // Takes full location as an argument and returns the nested upload folder location
    buildFileLocation(location) {
      if (!location) {
        return null;
      }
      return `${process.env.VUE_APP_API_URL}${location.replace('api/library', 'library/download')}`;
    },

    insertImage(editor) {
      // Create a unique Id to listen to incoming events for this instance of the image selector.
      const imageGuid = createUuid();

      // Update guid. (Prop passed to image preview.)
      this.guid = imageGuid;

      EventBus.$on(`image-select:${imageGuid}`, (data) => {
        // Image selected, remove the event listener, close the modal.
        EventBus.$off(`image-select:${imageGuid}`);
        this.imageModal = false;

        const range = editor.selection.getRng();
        const newNode = editor.getDoc().createElement('img'); // create img node in the editor
        newNode.src = data.src; // add src attribute

        if (data.meta.alt) {
          newNode.alt = data.meta.alt; // Conditionally include the meta tag for the image (read from library)
        }

        range.insertNode(newNode);

        // Broadcast the change
        this.broadcast();
      });
      this.imageModal = true;
    },

    setModalContext(context = null, show = false) {
      this.modalContext = context === 'meta' ? 'meta' : this.guid;
      this.imageModal = !!show;
    },

    tinyConfig() {
      return {
        branding: false,
        browser_spellcheck: true,
        plugins: ['link', 'lists'],
        toolbar: [
          'undo redo | bullist numlist | styleselect | bold italic | link image alignleft aligncenter alignright | imageSelect | code',
        ],
        templates: [
          {
            title: 'Name',
            description: 'Users full name',
            content: `${this.$store.state.meStore.me.name.firstname} ${this.$store.state.meStore.me.name.surname}`,
          },
        ],

        setup: (editor) => {
          editor.addButton('imageSelect', {
            tooltip: 'Select an image from the library',
            icon: 'books',
            onclick: () => {
              this.insertImage(editor);
            },
          });
        },
        pagebreak_split_block: true,
      };
    },

    // Formatter passed to flattenObject function.
    formatField(key, value) {
      // Format on common data types
      let formatted;
      switch (value) {
        case true:
          formatted = 1;
          break;
        case false:
          formatted = 0;
          break;
        default:
          formatted = value;
      }

      // Format specific fields
      switch (key) {
        case 'summary_points':
          formatted = value.map(x => x.text);
          break;
        case 'contacts':
          formatted = value.map(x => x.id);
          break;
        case 'tags':
        case 'instructors':
          formatted = value.filter(instructor => instructor.value !== 'external').map(x => x.id);
          break;
        case 'external_instructors':
          // These are handled seperately
          formatted = null;
          break;
        case 'datetime_online':
          formatted = moment(value)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss');
          break;
        case 'image_location':
        case 'meta_og_image_location':
          formatted = value?.replace('api/', '');
          break;
        default:
      }
      return formatted;
    },

    requestParams() {
      // Default formatting
      const params = this.$util.flattenObject(this.data, null, {}, this.formatField);

      if (params.program && params.program.length > 0) {
        params.program.forEach((item, index) => {
          params[`program_title[${index}]`] = params.program[index].title;
          params[`program_start[${index}]`] = moment(params.program[index].datetime)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss');
        });
        delete params.program;
      }

      // Manually add custom instructor data to keep array indices
      this.customInstructors.forEach((instructor, index) => {
        params[`external_instructor[${index}][firstname]`] = instructor.firstname;
        params[`external_instructor[${index}][lastname]`] = instructor.lastname;
        params[`external_instructor[${index}][email]`] = instructor.email;
      });

      if (params.status === 'draft') {
        params.datetime_online = null;
      }

      if (params.sort === 'webinar') {
        params.location_id = null;
      }

      // Remove null & undefined values
      for (const [key, value] of Object.entries(params)) {
        // If the value is not null, undefined or empty, remove it from the params.
        if (value === null || value === undefined) delete params[key];
      }

      return params;
    },

    getTargetOptions() {
      return this.axios
        .get('target')
        .then((res) => {
          this.targetOptions = res.data
            && res.data.targets
            && res.data.targets.map(target => ({ label: target.label, value: target.id }));
        })
        .catch((err) => {
          console.warn(err);
        });
    },

    setUrl() {
      if (this.data.title) {
        this.urlSet = true;
      }
    },

    /**
     * Search for location
     */
    autocompleteLocation(terms, done) {
      // Reset location on input
      this.axios.get(`location?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.locations.map(item => ({
          label: item.name,
          id: item.id,
          value: item.id,
        }));
        done(suggestions);
      });
    },

    /**
     * Search for certificate
     */
    autocompleteCertificate(terms, done) {
      // Reset location on input
      this.axios.get(`certificate?autocomplete=${terms}`).then((response) => {
        const suggestions = response.data.certificates.map(item => ({
          label: item.name,
          id: item.id,
          value: item.id,
        }));
        done(suggestions);
      });
    },

    selectCertificate(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      this.data.certificate.name = found.label;
      this.data.certificate.id = found.id;
    },

    selectLocation(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;
      this.data.location.name = found.label;
      this.data.location.id = found.id;

      // Workaround for vuelidate (requiredIf does not detect changes to this.data.sort when validation already has error)
      const tmp = this.data.sort;
      this.data.sort = null;
      this.$nextTick(() => {
        this.data.sort = tmp;
      });
    },

    removeCourse() {
      // Dialog
      this.$q.dialog({
        title: '',
        message: this.getString('module.library.delete.title.single'),
        ok: this.$store.state.stringsStore.strings.dialog.confirm.ok,
        cancel: this.$store.state.stringsStore.strings.dialog.confirm.cancel,
      }).then(() => {
        // delete call
        this.deleteCourse(this.$route.params.id).then(() => {
          this.$q.notify({
            message: this.getString(null, 'Education successfully removed', 'Opleiding succesvol verwijderd'),
            timeout: 2000,
            type: 'positive',
            position: 'top-right',
          });
          this.$router.push('/course');
        });
      }).catch(() => {
        console.warn('Not removed');
      });
    },
  },

  computed: {
    codeUrl() {
      return `${process.env.VUE_APP_SITE}college/${this.data.url}`;
    },
    // Url used by the uploader
    apiUrl() {
      return `course/${this.$route.params.id}/upload`;
    },
    url: {
      get() {
        return this.data.url;
      },
      set(value) {
        this.data.url = this.cleanUrl(value);
      },
    },
    webinar_url: {
      get() {
        return this.data.webinar_url;
      },
      set(value) {
        this.data.webinar_url = this.cleanUrl(value);
      },
    },
    instructorSelection: {
      get() {
        return this.data.instructors && this.data.instructors.length
          ? this.data.instructors.map(instructor => ({
            name: instructor.label || `${instructor.name.firstname} ${instructor.name.lastname}`,
          })) : [];
      },
      set(value) {
        this.data.instructors = value;
      },
    },
  },

  async mounted() {
    this.readOnly = false;

    await this.getTargetOptions();

    if (this.$route.params.id) {
      this.readOnly = true;
      this.data = await this.getCourse(this.$route.params.id);

      // Ensure validation works
      this.data.image = this.data.image || {
        location: null,
      };
    }

    this.loaded = true;

    EventBus.$on('chips-autocomplete:tags', (selection) => {
      this.data.tags = selection;
    });

    EventBus.$on('chips-autocomplete:contacts', (selection) => {
      this.data.contacts = selection;
    });

    // Event emitted when the upload is successful.
    EventBus.$on('refresh-table', () => {
      this.refreshFiles();
    });

    EventBus.$on(`file-select:${this.guid}`, (data) => {
      if (data.type === 'image') {
        this.imageSelected = true;
        if (this.data.image) {
          this.data.image.location = data.location;
        } else {
          // Added in case of an incoming post where image is null
          this.data.image = { location: data.location };
        }
      }

      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on('file-select:meta', (data) => {
      this.data.meta.og.image = { location: data.location, name: data.name };
      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on('file-select-close', () => {
      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on('chips-autocomplete:instructors', (selection) => {
      this.instructorKey += 0;
      this.$set(this.data, 'instructors', selection);
    });
  },

  destroyed() {
    EventBus.$off();
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables'
.full-height
  height: 100%

.image-preview.image {
  padding: 0;
  height: 200px;
  background-image: linear-gradient(45deg, #F3F3F3 25%, transparent 5%), linear-gradient(-45deg, #F3F3F3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F3F3F3 75%), linear-gradient(-45deg, transparent 75%, #F3F3F3 75%);
}

.centered {
  text-align: center;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.image-preview {
  height: 40vh;
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}
.flex-center {
  display: flex;
  justify-content: center;
}
table {
  font-family: arial, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

td, th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}

tr:nth-child(even) {
  background-color: #F1F1F1;
}
.border-solid {
    border-style: solid;
}
.field-error {
    font-size: 12px;
    color: $ctrl-red;
}
</style>
